import React, { useContext, useState } from "react";
import { ObjectContext } from "@providers/ObjectProvider";
import Avatar from '@mui/joy/Avatar';
import Chip from '@mui/joy/Chip';
import Box from '@mui/joy/Box';
import Button from '@mui/joy/Button';
import ButtonGroup from '@mui/joy/ButtonGroup';
import Card from '@mui/joy/Card';
import CardContent from '@mui/joy/CardContent';
import CardOverflow from '@mui/joy/CardOverflow';
import CardActions from '@mui/joy/CardActions';
import IconButton from '@mui/joy/IconButton';
import Typography from '@mui/joy/Typography';
import SvgIcon from '@mui/joy/SvgIcon';
import SettingsRoundedIcon from '@mui/icons-material/SettingsRounded';
import DeleteRoundedIcon from '@mui/icons-material/DeleteRounded';
import AddAlarmRoundedIcon from '@mui/icons-material/AddAlarmRounded';
import GridViewRoundedIcon from '@mui/icons-material/GridViewRounded';
import ViewListRoundedIcon from '@mui/icons-material/ViewListRounded';
import Tooltip from '@mui/joy/Tooltip';
import Table from '@mui/joy/Table';
import Stack from '@mui/joy/Stack';
import ObjectRelationshipCard from '@components/objects/ObjectRelationshipCard';
import { gradientTint } from "@utils/gradientTint";
import { sortByDate } from "@utils/sorters";
import ObjectPreview from "./ObjectPreview";
import { formatDate, isValidDate } from "@utils/dates";
import { dueDateClassForTask } from "@utils/dueDateClassForTask";
import { groupBy } from "@utils/groupBy";
import { useLocalStorage } from "@uidotdev/usehooks";
import { Grid, ToggleButtonGroup } from "@mui/joy";
import { FileThumbnail } from "@components/FileThumbnail";

interface ColumnWithValue {
  label: string;
  value: string;//|number|null;
  transformer?: never;
}
interface ColumnWithTransformer {
  label: string;
  value?: never;
  transformer: Function;
}

type IColumn = ColumnWithValue | ColumnWithTransformer;

export default function ObjectRelationshipFiles({
  relationship = [],
  title,
  tint = null,
  // columns,
  collapsible = false,
}) {
  const [isExpanded, setIsExpanded] = useState(false);
  const [fileLayout, setFileLayout] = useLocalStorage('file_layout', 'list');

  const perPage = 5;

  const canCollapse = collapsible && relationship.length > perPage;
  const isEmpty = relationship.length === 0;

  // TODO: allow switching between grid/list view
  return (
    <ObjectRelationshipCard
      unpadded
      empty={isEmpty}
      title={
        <CardOverflow
          sx={theme => ({
            borderTopLeftRadius: 'calc(var(--Card-childRadius) + var(--variant-borderWidth))',
            borderTopRightRadius: 'calc(var(--Card-childRadius) + var(--variant-borderWidth))',
            marginBlockStart: 'var(--CardOverflow-offset)',
            ...gradientTint(theme, tint),
          })}
        >
          <CardActions
            sx={theme => ({
              py: 1.5,
              // pointerEvents: canCollapse ? 'all' : 'none',
              cursor: 'pointer',
              userSelect: 'none',
              ':hover .pagination': {
                backgroundColor: '#FFFFFFB8',
              },
            })}
            onClick={() => setIsExpanded(!isExpanded)}
          >
            <Stack
              direction="row"
              justifyContent="space-between"
              alignItems="center"
              spacing={0.5}
              sx={{flex: 1}}
            >
              <Typography sx={theme => ({flex: 1, fontWeight: '600', color: 'white' })}>{title}</Typography>
              {!isEmpty && (
              <ToggleButtonGroup
                value={fileLayout}
                size="sm"
                color="neutral"
                // variant="outline"
                onChange={(e:MouseEvent, val:string) => setFileLayout(val)}
                sx={theme => ({
                  '--variant-outlinedHoverBg': theme.palette.background.surface,
                  '--variant-outlinedActiveBg': theme.palette.background.surface,
                  '--Icon-color': theme.palette.background.surface,
                  '--IconButton-size': '1.65rem',
                  borderRadius: '4px',
                  '[aria-pressed="true"] svg, .MuiIconButton-root:hover svg': {
                    color: theme.palette.neutral.outlinedColor,
                  },
                  'svg': {
                    width: '1.05rem',
                    height: '1.05rem',
                    color: theme.palette.background.level3,
                  },
                })}
              >
                <Tooltip title="List View">
                  <IconButton value="list">
                    <ViewListRoundedIcon/>
                  </IconButton>
                </Tooltip>
                <Tooltip title="Grid View">
                  <IconButton value="grid">
                    <GridViewRoundedIcon/>
                  </IconButton>
                </Tooltip>
              </ToggleButtonGroup>
              )}
            </Stack>
          </CardActions>
        </CardOverflow>
      }
    >
      {/* <CardOverflow> */}
      {fileLayout === 'list' && (
        <Table
          stickyHeader
          stripe="odd"
          // hoverRow
          sx={{
            margin: 0,
            padding: 0,
            tableLayout: 'auto',
          }}
        >
          <thead>
            <tr>
              <th>File</th>
            </tr>
          </thead>
          <tbody>
            {relationship.map((file, fileIndex:number) => (
              <tr key={fileIndex}>
                <td>
                  <FileThumbnail file={file} layout={fileLayout} />
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
      )}
      {fileLayout === 'grid' && (
        <Box
          sx={{
            display: 'grid',
            gap: 2,
            p: 2,
            gridTemplateColumns: {
              xs: 'repeat(2, 1fr)',
              md: 'repeat(3, 1fr)',
              // xl: 'repeat(4, 1fr)',
            }
          }}
        >
          {/* <Box key={fileIndex}> */}
          {relationship.map((file, fileIndex:number) => (
            <FileThumbnail key={fileIndex} file={file} layout={fileLayout} />
          ))}
          {/* </Box> */}
        </Box>
      )}

      {/* </CardOverflow> */}

    </ObjectRelationshipCard>
  );
}
